/**
 * System Diagnostics Page
 * 
 * Access via: /admin/diagnostics
 * Shows backend health, MongoDB connection status, and system info.
 * Useful for debugging deployment issues.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, CheckCircle, XCircle, AlertTriangle, RefreshCw, 
  Server, Database, Globe, Clock, Wifi, WifiOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemDiagnostics() {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({});
  const [lastCheck, setLastCheck] = useState(null);

  const runDiagnostics = async () => {
    setLoading(true);
    const diagnosticResults = {
      timestamp: new Date().toISOString(),
      frontend: { status: 'ok', url: window.location.origin },
      backend_url: API_URL,
      checks: {}
    };

    // Test 1: Basic backend ping
    try {
      const startTime = Date.now();
      const response = await fetch(`${API_URL}/api/health`, { 
        method: 'GET',
        signal: AbortSignal.timeout(10000)
      });
      const latency = Date.now() - startTime;
      
      if (response.ok) {
        const data = await response.json();
        diagnosticResults.checks.backend_health = {
          status: 'ok',
          latency: `${latency}ms`,
          response: data
        };
      } else {
        diagnosticResults.checks.backend_health = {
          status: 'error',
          latency: `${latency}ms`,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }
    } catch (error) {
      diagnosticResults.checks.backend_health = {
        status: 'error',
        error: error.message || 'Backend unreachable'
      };
    }

    // Test 2: Detailed system status
    try {
      const response = await fetch(`${API_URL}/api/system/status`, {
        method: 'GET',
        signal: AbortSignal.timeout(15000)
      });
      if (response.ok) {
        const data = await response.json();
        diagnosticResults.checks.system_status = {
          status: 'ok',
          response: data
        };
      } else {
        const text = await response.text();
        diagnosticResults.checks.system_status = {
          status: 'error',
          error: `HTTP ${response.status}`,
          details: text.substring(0, 500)
        };
      }
    } catch (error) {
      diagnosticResults.checks.system_status = {
        status: 'error',
        error: error.message
      };
    }

    // Test 3: Database connection check
    try {
      const response = await fetch(`${API_URL}/api/system/db-check`, {
        method: 'GET',
        signal: AbortSignal.timeout(15000)
      });
      if (response.ok) {
        const data = await response.json();
        diagnosticResults.checks.database = {
          status: data.connected ? 'ok' : 'error',
          response: data
        };
      } else {
        diagnosticResults.checks.database = {
          status: 'error',
          error: `HTTP ${response.status}`
        };
      }
    } catch (error) {
      diagnosticResults.checks.database = {
        status: 'error',
        error: error.message
      };
    }

    // Test 4: Auth endpoint check
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'test@test.com', password: 'test' }),
        signal: AbortSignal.timeout(10000)
      });
      // We expect 401 (invalid credentials) - that means the endpoint is working
      diagnosticResults.checks.auth_endpoint = {
        status: response.status === 401 || response.status === 400 ? 'ok' : 'warning',
        http_status: response.status,
        note: response.status === 401 || response.status === 400 
          ? 'Auth endpoint responding correctly' 
          : `Unexpected status: ${response.status}`
      };
    } catch (error) {
      diagnosticResults.checks.auth_endpoint = {
        status: 'error',
        error: error.message
      };
    }

    setResults(diagnosticResults);
    setLastCheck(new Date());
    setLoading(false);
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const StatusIcon = ({ status }) => {
    if (status === 'ok') return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (status === 'warning') return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getOverallStatus = () => {
    if (!results.checks) return 'unknown';
    const statuses = Object.values(results.checks).map(c => c.status);
    if (statuses.every(s => s === 'ok')) return 'healthy';
    if (statuses.some(s => s === 'error')) return 'unhealthy';
    return 'degraded';
  };

  const overallStatus = getOverallStatus();

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-6 w-6 text-blue-600" />
                  System Diagnostics
                </CardTitle>
                <CardDescription>
                  Backend health and connectivity checks
                </CardDescription>
              </div>
              <Button onClick={runDiagnostics} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Checking...' : 'Re-run Checks'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Overall Status */}
            <div className={`p-4 rounded-lg ${
              overallStatus === 'healthy' ? 'bg-green-50 border border-green-200' :
              overallStatus === 'degraded' ? 'bg-yellow-50 border border-yellow-200' :
              overallStatus === 'unhealthy' ? 'bg-red-50 border border-red-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                {overallStatus === 'healthy' ? (
                  <Wifi className="h-8 w-8 text-green-500" />
                ) : overallStatus === 'unhealthy' ? (
                  <WifiOff className="h-8 w-8 text-red-500" />
                ) : (
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                )}
                <div>
                  <div className="font-semibold text-lg">
                    {overallStatus === 'healthy' ? 'All Systems Operational' :
                     overallStatus === 'degraded' ? 'Degraded Performance' :
                     overallStatus === 'unhealthy' ? 'System Issues Detected' :
                     'Checking...'}
                  </div>
                  {lastCheck && (
                    <div className="text-sm text-gray-500">
                      Last check: {lastCheck.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between py-1 border-b">
              <span className="text-gray-600">Frontend URL:</span>
              <code className="bg-gray-100 px-2 rounded">{results.frontend?.url || window.location.origin}</code>
            </div>
            <div className="flex justify-between py-1 border-b">
              <span className="text-gray-600">Backend API URL:</span>
              <code className="bg-gray-100 px-2 rounded">{API_URL || 'Not configured'}</code>
            </div>
          </CardContent>
        </Card>

        {/* Individual Checks */}
        {results.checks && Object.entries(results.checks).map(([name, check]) => (
          <Card key={name}>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2">
                  {name === 'database' ? <Database className="h-4 w-4" /> :
                   name === 'backend_health' ? <Server className="h-4 w-4" /> :
                   <Clock className="h-4 w-4" />}
                  {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <StatusIcon status={check.status} />
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              {check.status === 'ok' ? (
                <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                  ✓ {check.note || 'Check passed'}
                  {check.latency && <span className="ml-2 text-gray-500">({check.latency})</span>}
                </div>
              ) : (
                <Alert className="bg-red-50 border-red-200">
                  <AlertDescription className="text-red-800">
                    <strong>Error:</strong> {check.error}
                    {check.details && (
                      <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-x-auto">
                        {check.details}
                      </pre>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Show response details if available */}
              {check.response && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    View response details
                  </summary>
                  <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(check.response, null, 2)}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Troubleshooting Tips */}
        {overallStatus === 'unhealthy' && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-sm text-red-700">Troubleshooting Tips</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p>If backend is unreachable (520 error):</p>
              <ol className="list-decimal ml-4 space-y-1 text-gray-700">
                <li><strong>MongoDB IP Whitelist:</strong> Ensure 0.0.0.0/0 is allowed in MongoDB Atlas → Network Access</li>
                <li><strong>Environment Variables:</strong> Check MONGO_URL is correctly set in backend .env</li>
                <li><strong>Redeploy:</strong> Try redeploying the application in Emergent UI</li>
                <li><strong>Contact Support:</strong> If issues persist, contact Emergent support with this diagnostic output</li>
              </ol>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
